import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, Palette, Building2, User, Lock } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FONTS = ["Manrope", "Poppins", "Inter", "Merriweather", "Playfair Display"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Branding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const canBrand = user ? user.can_brand !== false : true;

  useEffect(() => { api.get("/branding").then((r) => setB(r.data)); }, []);

  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/branding", b);
      setB(data);
      toast.success("Branding saved — applied to all your itineraries.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", `${BACKEND}${data.url}`);
      toast.success("Logo uploaded.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!b) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Agency branding</h1>
        <p className="text-slate-500 mt-1">Applied automatically across every itinerary you create.</p>

        <section className="mt-8 rounded-lg border border-slate-200 bg-white p-6 space-y-5" data-testid="branding-form">
          <div>
            <Label className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> Agency name</Label>
            <Input data-testid="brand-agency" value={b.agency_name || ""} onChange={(e) => set("agency_name", e.target.value)} className="mt-1" placeholder="Wanderlust Travel" />
          </div>

          <div>
            <Label className="inline-flex items-center gap-2"><Upload className="h-4 w-4 text-slate-400" /> Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {b.logo_url ? <img src={b.logo_url} alt="Your uploaded brand logo preview" className="h-12 w-12 object-contain border border-slate-200 rounded-md bg-white p-1" data-testid="brand-logo-preview" /> : <div className="h-12 w-12 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-slate-300 text-xs">none</div>}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="brand-logo-file" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-slate-200">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}
              </Button>
            </div>
            <Input data-testid="brand-logo-url" value={b.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className="mt-2 font-pnr text-xs" placeholder="…or paste a logo image URL" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="inline-flex items-center gap-2"><Palette className="h-4 w-4 text-slate-400" /> Primary color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={b.primary_color || "#2563EB"} onChange={(e) => set("primary_color", e.target.value)} className="h-9 w-12 rounded border border-slate-200" data-testid="brand-primary-color" />
                <Input value={b.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="font-pnr text-xs" />
              </div>
            </div>
            <div>
              <Label>Font</Label>
              <Select value={b.font || "Manrope"} onValueChange={(v) => set("font", v)}>
                <SelectTrigger className="mt-1" data-testid="brand-font"><SelectValue /></SelectTrigger>
                <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label className="inline-flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> Agent name</Label><Input data-testid="brand-agent-name" value={b.agent_name || ""} onChange={(e) => set("agent_name", e.target.value)} className="mt-1" /></div>
            <div><Label>Agent email</Label><Input data-testid="brand-agent-email" value={b.agent_email || ""} onChange={(e) => set("agent_email", e.target.value)} className="mt-1" /></div>
            <div><Label>Agent phone</Label><Input data-testid="brand-agent-phone" value={b.agent_phone || ""} onChange={(e) => set("agent_phone", e.target.value)} className="mt-1" /></div>
          </div>

          {canBrand ? (
            <Button data-testid="brand-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save branding"}
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Button data-testid="brand-save-locked" onClick={() => navigate("/dashboard")} className="bg-amber-500 hover:bg-amber-600 text-white">
                <Lock className="h-4 w-4 mr-1.5" /> Upgrade to Plus
              </Button>
              <span className="text-sm text-slate-500">Agency branding is a Plus feature — upgrade to save and apply it to your itineraries.</span>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
